import Interceptor from "./Interceptor";

// ==================== Deployment Operations ====================

/**
 * Get all deployments with filtering
 * @param {Object} params - { siteId, status, page, limit }
 * @returns {Promise}
 */
export const getAllDeployments = async (params = {}) => {
  try {
    const response = await Interceptor.get("/deployments", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get deployment by ID
 * @param {string} deploymentId
 * @returns {Promise}
 */
export const getDeploymentById = async (deploymentId) => {
  try {
    const response = await Interceptor.get(`/deployments/${deploymentId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get deployment logs
 * @param {string} deploymentId
 * @param {boolean} includeDiff - Whether to include actual file diffs
 * @returns {Promise}
 */
export const getDeploymentLogs = async (deploymentId, includeDiff = false) => {
  try {
    const response = await Interceptor.get(
      `/git/deployments/${deploymentId}/logs`,
      { params: { includeDiff } },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Cancel a running deployment
 * @param {string} deploymentId
 * @returns {Promise}
 */
export const cancelDeployment = async (deploymentId) => {
  try {
    const response = await Interceptor.post(
      `/deployments/${deploymentId}/cancel`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Retry a failed deployment
 * @param {string} deploymentId
 * @returns {Promise}
 */
export const retryDeployment = async (deploymentId) => {
  try {
    const response = await Interceptor.post(
      `/deployments/${deploymentId}/retry`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ==================== Deployment Analytics ====================

/**
 * Get deployment analytics summary
 * @param {string} siteId
 * @param {Object} params - { startDate, endDate }
 * @returns {Promise}
 */
export const getDeploymentAnalytics = async (siteId, params = {}) => {
  try {
    const response = await Interceptor.get(`/git/analytics/${siteId}/summary`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get deployment trends
 * @param {string} siteId
 * @param {Object} params - { period, startDate, endDate }
 * @returns {Promise}
 */
export const getDeploymentTrends = async (siteId, params = {}) => {
  try {
    const response = await Interceptor.get(`/git/analytics/${siteId}/trends`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get deployment comparison
 * @param {string} siteId
 * @param {Object} params - { period }
 * @returns {Promise}
 */
export const getDeploymentComparison = async (siteId, params = {}) => {
  try {
    const response = await Interceptor.get(
      `/git/analytics/${siteId}/comparison`,
      { params },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get environment analytics
 * @param {string} siteId
 * @returns {Promise}
 */
export const getEnvironmentAnalytics = async (siteId) => {
  try {
    const response = await Interceptor.get(
      `/git/analytics/${siteId}/environments`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ==================== Deployment Comparison ====================

/**
 * Compare two deployments
 * @param {string} deployment1Id
 * @param {string} deployment2Id
 * @returns {Promise}
 */
export const compareDeployments = async (deployment1Id, deployment2Id) => {
  try {
    const response = await Interceptor.get(
      `/git/deployments/${deployment1Id}/compare/${deployment2Id}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ==================== Deployment Scheduling ====================

/**
 * Schedule a deployment
 * @param {Object} payload - { configId, scheduledFor, branch, environment }
 * @returns {Promise}
 */
export const scheduleDeployment = async (payload) => {
  try {
    const response = await Interceptor.post("/git/schedule", payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get scheduled deployments
 * @param {Object} params - { siteId, status }
 * @returns {Promise}
 */
export const getScheduledDeployments = async (params = {}) => {
  try {
    const response = await Interceptor.get("/git/schedule", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update scheduled deployment
 * @param {string} scheduleId
 * @param {Object} payload - { scheduledFor, branch }
 * @returns {Promise}
 */
export const updateScheduledDeployment = async (scheduleId, payload) => {
  try {
    const response = await Interceptor.put(
      `/git/schedule/${scheduleId}`,
      payload,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Cancel scheduled deployment
 * @param {string} scheduleId
 * @returns {Promise}
 */
export const cancelScheduledDeployment = async (scheduleId) => {
  try {
    const response = await Interceptor.delete(`/git/schedule/${scheduleId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ==================== Export all ====================
const DeploymentService = {
  getAllDeployments,
  getDeploymentById,
  getDeploymentLogs,
  cancelDeployment,
  retryDeployment,
  getDeploymentAnalytics,
  getDeploymentTrends,
  getDeploymentComparison,
  getEnvironmentAnalytics,
  compareDeployments,
  scheduleDeployment,
  getScheduledDeployments,
  updateScheduledDeployment,
  cancelScheduledDeployment,
};

export default DeploymentService;
